import { oc } from "@orpc/contract";
import { z } from "zod";
import { IntegrationBaseSchema, IntegrationKey } from "./base";

/**
 * Microsoft Teams Integration Settings Schema
 *
 * Auth uses a single multi-tenant Azure AD app registered in Gently's tenant.
 * Customer admins consent it into their tenant via the admin-consent endpoint;
 * we capture their `tenantId` on callback. We never see or store customer-side
 * client credentials — the clientId/secret live in backend env config.
 *
 * `defaultActivityType` controls which entry in the Teams app manifest's
 * `activities.activityTypes` is used when a workflow step doesn't override it.
 * Must match a type declared in the installed Teams app manifest, or Microsoft
 * Graph rejects the send.
 */
export const MicrosoftTeamsIntegrationSettingsSchema = z.object({
  tenantId: z.string().optional(),
  consentedAt: z.coerce.date().optional(),
  defaultActivityType: z.string().prefault("workflowNotification"),
});

export type MicrosoftTeamsIntegrationSettings = z.infer<
  typeof MicrosoftTeamsIntegrationSettingsSchema
>;

export const MicrosoftTeamsIntegrationSettingsSchemaWithoutBase = z.object({
  key: z.literal(IntegrationKey.MicrosoftTeams),
  settings: MicrosoftTeamsIntegrationSettingsSchema,
});

export const MicrosoftTeamsIntegrationSchema = IntegrationBaseSchema.merge(
  MicrosoftTeamsIntegrationSettingsSchemaWithoutBase,
);

export type MicrosoftTeamsIntegration = z.infer<
  typeof MicrosoftTeamsIntegrationSchema
>;

// ── Contracts ──

const beginAdminConsent = oc.output(
  z.object({
    consentUrl: z.string(),
  }),
);

const testConnection = oc
  .input(z.object({ integrationId: z.string().uuid() }))
  .output(
    z.object({
      success: z.boolean(),
      message: z.string(),
    }),
  );

const disconnect = oc
  .input(z.object({ integrationId: z.string().uuid() }))
  .output(z.object({ success: z.boolean() }));

export const microsoftTeamsContracts = {
  beginAdminConsent,
  testConnection,
  disconnect,
} as const;

export type MicrosoftTeamsContracts = typeof microsoftTeamsContracts;
